import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import * as Sentry from '@sentry/react'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { LanguageProvider } from '@/i18n/LanguageContext'
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthGuard, AdminGuard, PageGuard } from '@/components/shared/AuthGuard'
import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { Chat } from '@/pages/Chat'
import LeadsPage from '@/pages/Leads'
import { Broadcast } from '@/pages/Broadcast'
import CardBuilder from '@/pages/CardBuilder'
import { CaptureCards } from '@/pages/CaptureCards'
import { RespostaDM } from '@/pages/RespostaDM'
import { Push } from '@/pages/Push'
import { Sequences } from '@/pages/Sequences'
import { LeadRecovery } from '@/pages/LeadRecovery'
import { Poster } from '@/pages/Poster'
import { Media } from '@/pages/Media'
import { Instagram } from '@/pages/Instagram'
import { Engagement } from '@/pages/Engagement'
import { Admin } from '@/pages/Admin'
import { Monitor } from '@/pages/Monitor'
import { CTM } from '@/pages/CTM'
import { RecuperarComentarios } from '@/pages/RecuperarComentarios'
import { CheckerBot } from '@/pages/CheckerBot'
import { Financial } from '@/pages/Financial'
import { Pages } from '@/pages/Pages'
import { Settings } from '@/pages/Settings'
import { YouTube } from '@/pages/YouTube'
import { WhatsApp } from '@/pages/WhatsApp'
import { Status } from '@/pages/Status'
import { Extension } from '@/pages/Extension'
import { MediaDownload } from '@/pages/MediaDownload'
import { ShortLinks } from '@/pages/ShortLinks'
import { Sales } from '@/pages/Sales'
import { Tracker } from '@/pages/Tracker'
import { Creator } from '@/pages/Creator'
import { IgGrowth } from '@/pages/IgGrowth'
import { Exclusao } from '@/pages/Exclusao'
import { AutomacaoFB } from '@/pages/AutomacaoFB'
import { AdManager } from '@/pages/AdManager'
import { Adskeeper } from '@/pages/Adskeeper'
import { Transfer } from '@/pages/Transfer'
import { ResearchNotes } from '@/pages/ResearchNotes'
import { PermissionAudit } from '@/pages/PermissionAudit'
import { PageManager } from '@/pages/PageManager'
import { IgManager } from '@/pages/IgManager'
import { Telegram } from '@/pages/Telegram'
import { Emails } from '@/pages/Emails'
import { isReviewMode } from '@/lib/staging'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center h-screen text-white">Algo deu errado. Recarregue a página.</div>}>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="conectagran-theme" disableTransitionOnChange>
        <TooltipProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route element={<AuthGuard />}>
                <Route element={<AppLayout />}>
                  <Route element={<PageGuard />}>
                    <Route path="/" element={<Navigate to={isReviewMode() ? '/gerenciador' : '/painel'} replace />} />
                    {isReviewMode() ? (
                      <>
                        <Route path="/gerenciador" element={<PageManager />} />
                        <Route path="/broadcast" element={<Broadcast />} />
                        <Route path="*" element={<Navigate to="/gerenciador" replace />} />
                      </>
                    ) : (
                      <>
                    <Route path="/painel" element={<Dashboard />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/contatos" element={<LeadsPage />} />
                    <Route path="/broadcast" element={<Broadcast />} />
                    {/* OCULTA — sem link em menu. Só via URL direta. Card com imagem + 2 botões. */}
                    <Route path="/broadcast/card-builder" element={<CardBuilder />} />
                    <Route path="/card-de-captura" element={<CaptureCards />} />
                    <Route path="/instagram/card-captura" element={<CaptureCards key="ig" platform="ig" />} />
                    <Route path="/instagram/gerenciador" element={<IgManager />} />
                    <Route path="/instagram/sequencia" element={<Sequences key="ig" platform="ig" />} />
                    <Route path="/resposta-dm" element={<RespostaDM />} />
                    <Route path="/push" element={<Push />} />
                    <Route path="/emails" element={<Emails />} />
                    <Route path="/recuperar-comentarios" element={<RecuperarComentarios />} />
                    <Route path="/ctm" element={<CTM />} />
                    <Route path="/sequencias" element={<Sequences />} />
                    <Route path="/recuperacao-lead" element={<LeadRecovery />} />
                    <Route path="/publicar" element={<Poster />} />
                    <Route path="/midias" element={<Media />} />
                    <Route path="/instagram" element={<Instagram />} />
                    <Route path="/ig-crescimento" element={<IgGrowth />} />
                    <Route path="/engagement" element={<Engagement />} />
                    <Route path="/checker-bot" element={<CheckerBot />} />
                    <Route path="/financeiro" element={<Financial />} />
                    <Route path="/paginas" element={<Pages />} />
                    <Route path="/youtube" element={<YouTube />} />
                    <Route path="/ad-manager" element={<AdManager />} />
                    <Route path="/adskeeper" element={<Adskeeper />} />
                    <Route path="/whatsapp" element={<WhatsApp />} />
                    <Route path="/telegram" element={<Telegram />} />
                    <Route path="/extensao" element={<Extension />} />
                    <Route path="/automacao-fb" element={<AutomacaoFB />} />
                    <Route path="/download-midias" element={<MediaDownload />} />
                    <Route path="/encurtador" element={<ShortLinks />} />
                    <Route path="/exclusao" element={<Exclusao />} />
                    <Route path="/transfer" element={<Transfer />} />
                    <Route path="/rastreamento" element={<Tracker />} />
                    <Route path="/creator" element={<Creator />} />
                    <Route path="/notas" element={<ResearchNotes />} />
                    <Route path="/permission-audit" element={<PermissionAudit />} />
                    <Route path="/gerenciador" element={<PageManager />} />
                    <Route path="/configuracoes" element={<Settings />} />
                      </>
                    )}
                  </Route>

                  <Route path="/status" element={<Status />} />

                  <Route element={<AdminGuard />}>
                    <Route path="/vendas" element={<Sales />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/monitor" element={<Monitor />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </TooltipProvider>
      </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}
